/**
 * Cross-section geometry, mirroring src/Domain/Geometry/Geometry.php.
 *
 * COORDINATE CONVENTION: points are produced in the MATHS convention, +x right and
 * +y UP, exactly as the PHP implementation does. The SVG renderer flips y once, in
 * its transform, and nowhere else (SPEC_DEFECTS F-1).
 *
 * This is the one place the geometry algorithm is duplicated across languages, and
 * it is duplicated deliberately: redrawing on every keystroke cannot wait for a
 * round trip. The duplication is safe only because both implementations are pinned
 * to tests/fixtures/geometry/cases.json, which neither language owns. Pricing is
 * NOT duplicated — that would be standing rule 2 broken (DECISIONS D8).
 */

export interface Point {
	readonly x: number;
	readonly y: number;
}

export interface BoundingBox {
	readonly minX: number;
	readonly minY: number;
	readonly maxX: number;
	readonly maxY: number;
	readonly width: number;
	readonly height: number;
}

export interface CrossSection {
	readonly points: Point[];
	readonly developedWidthMm: number;
	readonly bendCount: number;
	readonly boundingBox: BoundingBox;
	/** Visible stubs for folded edges, keyed by segment index. */
	readonly hems: Record<number, readonly [Point, Point]>;
}

const FLAT_ANGLE_DEG = 180;

/**
 * How far a hem is drawn from the face it is folded against, as a fraction of the
 * part's larger dimension, at its FREE EDGE only — the stub hinges on the fold it
 * shares with the outline. Mirrors Geometry::HEM_DRAW_GAP.
 *
 * DELIBERATELY NOT TO SCALE, and the only part of this module that is not. The hem is
 * closed — folded flat with no gap — so drawn truthfully it is one sheet thickness away,
 * 0,5 mm, which on a 280 mm part rendered 320 px wide is under half a pixel. A truthful
 * hem is an invisible hem, and invisible is indistinguishable from absent, which is the
 * defect this fixes. Scaled to the part so a small laštakis and a wide palangė show it at
 * the same apparent size.
 */
const HEM_DRAW_GAP = 0.02;

/** An unpressed hem springs this much further open. Mirrors Geometry. */
const HEM_OPEN_GAP_FACTOR = 2;

/** Swing ceilings: a hem must read as a fold, never as a flange. Mirrors Geometry. */
const HEM_CLOSED_MAX_SWING_DEG = 30;
const HEM_OPEN_MAX_SWING_DEG = 45;

/**
 * Lays out the cross-section polyline.
 *
 * Deductions are not applied here. The shipped deduction table is empty and the
 * authoritative developed width comes back from the server with the price; this
 * function exists to draw, and drawing does not need sub-millimetre accuracy.
 */
export function crossSection(
	segmentsMm: readonly number[],
	anglesDeg: readonly number[],
	directions: readonly number[],
	segmentIsHem: readonly boolean[] = [],
	initialHeadingDeg = 0,
	/*
	 * Per bend: the stored angle is the DEFLECTION (the angle the metal is bent
	 * through) rather than the interior between the faces. The owner's sheets mix
	 * the two conventions per part, and every stored number is the sheet's own —
	 * mirrors Geometry::cross_section, pinned by the shared fixtures.
	 */
	angleIsDeflection: readonly boolean[] = [],
	/*
	 * Which hems are folded back but not pressed flat (round 4: closure is a
	 * per-hem manufacturing property, and the customer approves the drawing, so
	 * the two states must look different). Mirrors Geometry::cross_section.
	 */
	hemIsOpen: readonly boolean[] = []
): CrossSection {
	/*
	 * Presentation only — mirrors Geometry::$initial_heading_deg. A fold is a full
	 * reversal, so the face after a leading hem is pinned exactly opposite it and every
	 * hemmed part came out rotated. Zero is the old behaviour.
	 */
	let heading = initialHeadingDeg;
	let x = 0;
	let y = 0;

	const points: Point[] = [{ x: 0, y: 0 }];
	const bendCount = anglesDeg.length;

	segmentsMm.forEach((widthMm, index) => {
		const radians = (heading * Math.PI) / 180;

		x += Math.cos(radians) * widthMm;
		// +y is up here; the renderer flips it. See the module comment and F-1.
		y += Math.sin(radians) * widthMm;

		points.push({ x, y });

		if (index < bendCount) {
			const stated = anglesDeg[index] ?? FLAT_ANGLE_DEG;
			// A deflection IS the turn; an interior is its supplement.
			const turn = angleIsDeflection[index] === true ? stated : FLAT_ANGLE_DEG - stated;

			heading += turn * (directions[index] ?? 1);
		}
	});

	const box = boundingBox(points);

	return {
		points,
		developedWidthMm: segmentsMm.reduce((sum, width) => sum + width, 0),
		bendCount,
		boundingBox: box,
		hems: hemStubs(points, directions, segmentIsHem, box, hemIsOpen),
	};
}

/**
 * The visible stub for each folded edge. Mirrors Geometry::hems().
 *
 * A closed hem is collinear with the face it is folded against — that is what closed
 * means — so the outline draws it as a line on top of a line and the part looks unhemmed.
 * These are the same segments, moved clear of the face so they can be seen.
 *
 * Which side it folds to comes from the fold's own bend direction, which is the
 * mechanically true answer. The owner has stated the hem folds toward the UNCOATED face,
 * so the profile's direction must be chosen to match — recorded against F-18.
 */
function hemStubs(
	points: readonly Point[],
	directions: readonly number[],
	segmentIsHem: readonly boolean[],
	box: BoundingBox,
	hemIsOpen: readonly boolean[] = []
): Record<number, readonly [Point, Point]> {
	const hems: Record<number, readonly [Point, Point]> = {};

	if (!segmentIsHem.some(Boolean)) {
		return hems;
	}

	const gap = Math.max(box.width, box.height) * HEM_DRAW_GAP;

	points.forEach((start, index) => {
		const end = points[index + 1];

		if (end === undefined || segmentIsHem[index] !== true) {
			return;
		}

		// A leading hem folds at bend 0; any other folds at the bend before it.
		const fold = index === 0 ? 0 : index - 1;
		const direction = directions[fold];

		if (direction === undefined) {
			return;
		}

		const dx = end.x - start.x;
		const dy = end.y - start.y;
		const length = Math.sqrt(dx * dx + dy * dy);

		if (length <= 0) {
			return;
		}

		/*
		 * The sign differs between a leading and a trailing hem because the walk
		 * traverses them in opposite senses: a leading hem is walked INTO the fold, a
		 * trailing one out of it. The same rotation for both would put one on the wrong
		 * face.
		 */
		const sense = (index === 0 ? 1 : -1) * direction;
		const nx = -(dy / length) * sense;
		const ny = (dx / length) * sense;

		/*
		 * Every hem hinges on its fold: the stub shares the fold vertex with the
		 * outline — they touch, because on the real part they are one piece of
		 * metal — and only the free edge lifts away, twice as far when the shop
		 * leaves the fold unpressed. Mirrors Geometry::hems().
		 */
		const open = hemIsOpen[index] === true;
		const target = open ? gap * HEM_OPEN_GAP_FACTOR : gap;
		const ceiling = Math.sin(
			((open ? HEM_OPEN_MAX_SWING_DEG : HEM_CLOSED_MAX_SWING_DEG) * Math.PI) / 180
		);

		const sine = Math.min(target / length, ceiling);
		const cosine = Math.sqrt(1 - sine * sine);

		const pivot = index === 0 ? end : start;
		const free = index === 0 ? start : end;

		const swung = {
			x: pivot.x + (free.x - pivot.x) * cosine + nx * length * sine,
			y: pivot.y + (free.y - pivot.y) * cosine + ny * length * sine,
		};

		hems[index] = index === 0 ? [swung, pivot] : [pivot, swung];
	});

	return hems;
}

/**
 * The letter the customer sees against each segment, or null where there is none.
 *
 * DISPLAY LETTERS ARE NOT IDENTIFIERS. The stored `id` in a profile definition is
 * identity and never changes, because changing it would break the one property it exists
 * to provide. The letter is a label, and it is numbered over the things the customer can
 * actually change — so a part whose first segment is a hem shows its first EDITABLE
 * segment as A.
 *
 * A locked segment gets no letter for the reason a fold gets no badge: a label is a
 * pointer to a control, and a label pointing at nothing is worse than a gap.
 *
 * MUST AGREE WITH THE PHP. `Configuration::display_letters()` is the same rule, because
 * the drawing and the controls are rendered here and the cart, order and email parameter
 * lists are rendered there — and a customer who says „B is wrong" has to mean one segment
 * on every screen. Both sides are tested.
 */
export function displayLetters(editable: readonly boolean[]): (string | null)[] {
	const letters: (string | null)[] = [];
	let shown = 0;

	for (const isEditable of editable) {
		if (!isEditable) {
			letters.push(null);
			continue;
		}

		// Past Z, fall back to a number rather than emit punctuation.
		letters.push(shown < 26 ? String.fromCharCode(65 + shown) : String(shown + 1));
		shown += 1;
	}

	return letters;
}

/** The extent of a point set. */
export function boundingBox(points: readonly Point[]): BoundingBox {
	const xs = points.map((point) => point.x);
	const ys = points.map((point) => point.y);
	const minX = Math.min(...xs);
	const minY = Math.min(...ys);
	const maxX = Math.max(...xs);
	const maxY = Math.max(...ys);

	return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/* ------------------------------------------------------------------------- *
 * The annotation layer.
 *
 * Everything below places letters, millimetre values and bend badges around the
 * outline, and then sizes the viewBox to whatever that came to. It is separate
 * from the model geometry above, which is pinned to the shared PHP fixtures and
 * must not move.
 * ------------------------------------------------------------------------- */

/** Metrics of the drawing's own annotation layer, in viewBox user units. */
export const DRAW = {
	/** Matches --sd-draw-label. */
	labelFont: 26,
	/** Matches --sd-draw-bend. */
	bendFont: 22,
	/** Matches the <circle r> behind a bend number. */
	discRadius: 11,
	/*
	 * Mean advance width per character at 1em for the semibold UI sans, across
	 * the characters these labels actually contain: A-L, the digits and a space.
	 * Deliberately generous -- over-estimating reserves too much space, which
	 * looks slightly airy; under-estimating lets two labels touch, which is the
	 * bug this exists to prevent.
	 */
	advance: 0.62,
	/** Clear space demanded between any two annotation boxes. */
	clearance: 7,
	/** Breathing room between the outermost annotation and the viewBox edge. */
	margin: 12,
} as const;

/**
 * Stroke weights in RENDERED PIXELS, applied with vector-effect:
 * non-scaling-stroke so the same part draws at the same weight wherever it
 * appears (owner, round 2, 2026-09-07 — weights still differed after a
 * viewBox-proportional attempt, because the CSS box's binding axis differs
 * between tall and wide parts). One number per surface; if the weight needs
 * adjusting it is adjusted here, once, for every profile at the same time.
 *
 * NOTE ON A DIFFERENT INTENT: stroke weight proportional to SHEET THICKNESS
 * (so 0,4 and 0,7 mm read differently) was part of the original design and is
 * not implemented anywhere. Every material today is 0,50 mm, so nothing is
 * currently distinguished by it and uniformity wins — but if a second
 * thickness ever ships, that intent needs a deliberate design on top of these
 * constants, not a quiet revert (HANDOVER S74/S75).
 */
export const STROKE = {
	/** The coloured outline of the main drawing. */
	main: 5,
	/** The thin dark overlay that keeps a pale colour legible. */
	hairline: 0.75,
	/** The profile-picker thumbnails. */
	thumb: 3,
} as const;

/** An axis-aligned box in projected space. */
interface Box {
	readonly minX: number;
	readonly minY: number;
	readonly maxX: number;
	readonly maxY: number;
}

/**
 * The box a piece of centred SVG text occupies.
 *
 * Both label kinds render with text-anchor=middle and dominant-baseline=central,
 * so the box is centred on the anchor rather than sitting to its right.
 */
function textBox(text: string, x: number, y: number, fontSize: number): Box {
	const halfWidth = (text.length * fontSize * DRAW.advance) / 2;
	const halfHeight = fontSize * 0.62;

	return { minX: x - halfWidth, minY: y - halfHeight, maxX: x + halfWidth, maxY: y + halfHeight };
}

/** The box a bend badge occupies: the disc, not the digit inside it. */
function discBox(x: number, y: number): Box {
	return {
		minX: x - DRAW.discRadius,
		minY: y - DRAW.discRadius,
		maxX: x + DRAW.discRadius,
		maxY: y + DRAW.discRadius,
	};
}

/**
 * Whether a drawn stroke passes through a box, with clearance to spare.
 *
 * BOXES ARE NOT ENOUGH FOR THE PART ITSELF. An outline segment is a long thin
 * thing; its bounding box is a large rectangle that a diagonal barely touches,
 * so testing box-against-box would forbid half the drawing while still letting a
 * label lie across the stroke it belongs to. This clips the segment against the
 * inflated box (Liang-Barsky), which is exact for what is actually drawn.
 */
function segmentHitsBox(p: Point, q: Point, box: Box, clearance: number): boolean {
	const minX = box.minX - clearance;
	const maxX = box.maxX + clearance;
	const minY = box.minY - clearance;
	const maxY = box.maxY + clearance;

	const dx = q.x - p.x;
	const dy = q.y - p.y;

	let enter = 0;
	let leave = 1;

	const edges: readonly (readonly [number, number])[] = [
		[-dx, p.x - minX],
		[dx, maxX - p.x],
		[-dy, p.y - minY],
		[dy, maxY - p.y],
	];

	for (const [slope, offset] of edges) {
		if (slope === 0) {
			// Parallel to this edge: outside it means the segment misses entirely.
			if (offset < 0) {
				return false;
			}

			continue;
		}

		const crossing = offset / slope;

		if (slope < 0) {
			enter = Math.max(enter, crossing);
		} else {
			leave = Math.min(leave, crossing);
		}
	}

	return enter <= leave;
}

/** Half the outline stroke's width: it is drawn centred, so it spills either side. */
const STROKE_HALF = 8;

/**
 * How many things a candidate annotation position runs into.
 *
 * A count rather than a boolean so a crowded part can still be laid out: when
 * nothing is completely clear, the least obstructed position is a real answer
 * and the last one tried is not.
 */
function obstructions(
	box: Box,
	placed: readonly Box[],
	strokes: readonly (readonly [Point, Point])[]
): number {
	let hits = 0;

	for (const other of placed) {
		if (intersects(box, other, DRAW.clearance)) {
			hits += 1;
		}
	}

	for (const [from, to] of strokes) {
		if (segmentHitsBox(from, to, box, STROKE_HALF + DRAW.clearance)) {
			hits += 1;
		}
	}

	return hits;
}

function intersects(a: Box, b: Box, clearance: number): boolean {
	return (
		a.minX - clearance < b.maxX &&
		a.maxX + clearance > b.minX &&
		a.minY - clearance < b.maxY &&
		a.maxY + clearance > b.minY
	);
}

function union(boxes: readonly Box[]): Box {
	return {
		minX: Math.min(...boxes.map((b) => b.minX)),
		minY: Math.min(...boxes.map((b) => b.minY)),
		maxX: Math.max(...boxes.map((b) => b.maxX)),
		maxY: Math.max(...boxes.map((b) => b.maxY)),
	};
}

/** A label anchored beside the shape it names. */
export interface Annotation {
	readonly text: string;
	readonly x: number;
	readonly y: number;
}

/** How to project a section. */
export interface ProjectOptions {
	/** Length of the shape's longer side once drawn, in user units. */
	readonly longEdge?: number;
	/**
	 * The letter to show against each segment, or null for none.
	 *
	 * Supplied rather than derived here, so the drawing and the input controls cannot
	 * disagree: they are given the same array. See `displayLetters`.
	 */
	readonly segmentLetters?: readonly (string | null)[];
	/** Millimetres shown after each segment letter. Omit to show the letter alone. */
	readonly segmentValuesMm?: readonly number[];
	/** false for a bare thumbnail with no lettering at all. */
	readonly annotate?: boolean;
}

/** Where the labels go, and the viewBox that contains all of it. */
export interface Projection {
	readonly path: string;
	/**
	 * The folded edges, as disconnected stubs in the same coordinate space as `path`.
	 * Separate because a closed hem lies against the outline rather than continuing
	 * from it, so it cannot be part of the same polyline.
	 */
	readonly hemPath: string;
	readonly segmentLabels: Annotation[];
	readonly bendLabels: Annotation[];
	/** Ready for the SVG viewBox attribute: fits the outline AND every label. */
	readonly viewBox: string;
	readonly width: number;
	readonly height: number;
	/**
	 * Every stroke of the PART in the same space as the annotations: outline
	 * segments and hem stubs. Exposed so "a label never touches the metal" can be
	 * asserted from the same geometry the layout avoided, rather than a test
	 * re-deriving the projection and proving only that it matches itself.
	 */
	readonly strokes: readonly (readonly [Point, Point])[];
}

/**
 * Projects the cross-section and lays out its annotations without collisions.
 *
 * WHY THIS IS BOX-BASED. The previous separation pass compared label ANCHOR
 * POINTS against a fixed clearance. That held while a label was a single letter
 * and broke the moment the letters gained their millimetre value: "A 30" is some
 * sixty units wide, so two anchors thirty units apart put the text straight
 * through the bend badge and it read as "A 301". Anchors do not collide; boxes
 * do, so boxes are what this measures.
 *
 * Badges are placed first and are immovable — a badge marks a specific vertex,
 * and sliding it would point at the wrong bend. Labels then search outward from
 * their own segment for the first position that clears everything already
 * placed. The search is a fixed list of candidates tried in a fixed order, so
 * the same part always draws identically.
 *
 * The viewBox is then sized to the union of everything, which is also why the
 * drawing no longer wastes half its canvas on guessed padding.
 */
export function project(section: CrossSection, options: ProjectOptions = {}): Projection {
	const longEdge = options.longEdge ?? 420;
	const annotate = options.annotate ?? true;
	const box = section.boundingBox;

	const scale = longEdge / Math.max(box.width, box.height, 1);

	const screen = section.points.map((point) => ({
		x: (point.x - box.minX) * scale,
		// The y flip. Model +y is up, SVG +y is down. This is the only place.
		y: (box.maxY - point.y) * scale,
	}));

	const path = screen
		.map(
			(point, index) =>
				`${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)},${point.y.toFixed(2)}`
		)
		.join(' ');

	const toScreen = (point: Point): Point => ({
		x: (point.x - box.minX) * scale,
		y: (box.maxY - point.y) * scale,
	});

	const hemPath = Object.values(section.hems)
		.map(([start, end]) => {
			const a = toScreen(start);
			const b = toScreen(end);

			return `M${a.x.toFixed(2)},${a.y.toFixed(2)} L${b.x.toFixed(2)},${b.y.toFixed(2)}`;
		})
		.join(' ');

	/*
	 * THE HEMS COUNT TOWARD THE EXTENT. They are part of the drawn part, not
	 * decoration, and they stand off the outline — an unpressed one by twice the
	 * nominal gap — so sizing the viewBox to the outline alone lets a fold clip at
	 * the edge. The customer approves this drawing; a hem cropped out of it is a hem
	 * they cannot check.
	 */
	const hemStubs: (readonly [Point, Point])[] = Object.values(section.hems).map(
		([a, b]) => [toScreen(a), toScreen(b)] as const
	);

	/*
	 * EVERY STROKE OF THE PART, as drawn. Annotations are laid out around these,
	 * not merely around each other: a number lying across the metal is as unreadable
	 * as two numbers lying across one another, and until now only the second was
	 * prevented.
	 */
	const strokes: (readonly [Point, Point])[] = [
		...screen.slice(0, -1).map((point, index) => [point, screen[index + 1]] as [Point, Point]),
		...hemStubs,
	];

	const drawn = [...screen, ...hemStubs.flatMap(([a, b]) => [a, b])];

	const occupied: Box[] = drawn.map((p) => ({
		minX: p.x - STROKE_HALF,
		minY: p.y - STROKE_HALF,
		maxX: p.x + STROKE_HALF,
		maxY: p.y + STROKE_HALF,
	}));

	if (!annotate) {
		const extent = union(occupied);
		const bareWidth = extent.maxX - extent.minX + DRAW.margin * 2;
		const bareHeight = extent.maxY - extent.minY + DRAW.margin * 2;

		return {
			path,
			hemPath,
			segmentLabels: [],
			bendLabels: [],
			viewBox: `${(extent.minX - DRAW.margin).toFixed(2)} ${(extent.minY - DRAW.margin).toFixed(2)} ${bareWidth.toFixed(2)} ${bareHeight.toFixed(2)}`,
			width: bareWidth,
			height: bareHeight,
			strokes,
		};
	}

	const centroidX = screen.reduce((sum, p) => sum + p.x, 0) / screen.length;
	const centroidY = screen.reduce((sum, p) => sum + p.y, 0) / screen.length;

	/* Badges first, and they do not move afterwards: a badge names a vertex. */
	const bendLabels: Annotation[] = [];
	const placed: Box[] = [];

	for (let i = 1; i < screen.length - 1; i++) {
		const vertex = screen[i];

		if (vertex === undefined) {
			continue;
		}

		/*
		 * NO BADGE ON A FOLD. Vertex i is bend i - 1, and a bend touching a hem is the
		 * fold — not an angle anybody chose. A badge is a pointer to a control, and the
		 * fold has no control, so badging it leaves a number on the drawing that refers
		 * to nothing.
		 *
		 * The hem itself stays drawn, and its width stays in the dimensions: the drawing
		 * is a picture of the part, and the 12 mm is a real dimension of it. What it is
		 * not is a parameter of the order.
		 */
		if (section.hems[i - 1] !== undefined || section.hems[i] !== undefined) {
			continue;
		}

		/*
		 * OUT OF THE BEND, NOT AWAY FROM THE CENTROID. The centroid ray is a decent
		 * guess on a compact part and a bad one on a shallow bend: at 175 degrees the
		 * part is nearly a straight line, the centroid sits almost on it, and the ray
		 * runs PARALLEL to the metal — so a badge stepping along it never escapes the
		 * stroke, which is what put 88 of the 864 swept geometries' badges on the
		 * outline. The bisector of the two edges meeting at this vertex is
		 * perpendicular to the metal exactly when the bend is shallow, which is when
		 * it matters; the centroid then only decides which of its two senses is the
		 * outside of the bend.
		 */
		const previous = screen[i - 1];
		const next = screen[i + 1];

		let outX = vertex.x - centroidX;
		let outY = vertex.y - centroidY;

		if (previous !== undefined && next !== undefined) {
			const inLength = Math.hypot(vertex.x - previous.x, vertex.y - previous.y) || 1;
			const outLength = Math.hypot(next.x - vertex.x, next.y - vertex.y) || 1;

			const bisectorX = (next.x - vertex.x) / outLength - (vertex.x - previous.x) / inLength;
			const bisectorY = (next.y - vertex.y) / outLength - (vertex.y - previous.y) / inLength;
			const bisector = Math.hypot(bisectorX, bisectorY);

			// Degenerate only on a perfectly straight join, where there is no bend to
			// badge and the centroid ray is as good an answer as any.
			if (bisector > 0.0001) {
				const sense =
					bisectorX * (vertex.x - centroidX) + bisectorY * (vertex.y - centroidY) >= 0
						? 1
						: -1;

				outX = (bisectorX / bisector) * sense;
				outY = (bisectorY / bisector) * sense;
			}
		}

		const away = Math.hypot(outX, outY) || 1;

		/*
		 * Out along the bisector, and if that is blocked, out to either side of it.
		 * One direction is not always enough: fold a part almost shut and the bend's
		 * own bisector runs down the middle of the two arms, so stepping along it
		 * stays between them however far it goes. The perpendiculars leave the
		 * sliver immediately, which is the whole point of having more than one.
		 */
		const unitX = outX / away;
		const unitY = outY / away;
		const escapes: readonly (readonly [number, number])[] = [
			[unitX, unitY],
			[-unitY, unitX],
			[unitY, -unitX],
		];

		let annotation = { text: String(i), x: vertex.x, y: vertex.y };
		let fewestHits = Infinity;

		for (const [dirX, dirY] of escapes) {
			for (let step = 0; step < 10; step++) {
				const distance = DRAW.discRadius + 5 + step * 9;
				const candidate = {
					text: String(i),
					x: vertex.x + dirX * distance,
					y: vertex.y + dirY * distance,
				};

				const hits = obstructions(discBox(candidate.x, candidate.y), placed, strokes);

				/*
				 * Keep the least obstructed position rather than the last one tried:
				 * on a crowded part every candidate can be blocked, and "last" is
				 * arbitrary where "least" is at least the best available.
				 */
				if (hits < fewestHits) {
					fewestHits = hits;
					annotation = candidate;
				}

				if (hits === 0) {
					break;
				}
			}

			if (fewestHits === 0) {
				break;
			}
		}

		bendLabels.push(annotation);
		placed.push(discBox(annotation.x, annotation.y));
	}

	/* Then the letters, which search outward for the first clear position. */
	const letters = 'ABCDEFGHIJKL';
	const segmentLabels: Annotation[] = [];

	for (let i = 0; i < screen.length - 1; i++) {
		const a = screen[i];
		const b = screen[i + 1];

		if (a === undefined || b === undefined) {
			continue;
		}

		const supplied = options.segmentLetters;
		const letter = supplied === undefined ? (letters[i] ?? String(i + 1)) : supplied[i];

		// null means this segment carries no label at all — see displayLetters.
		if (letter === null || letter === undefined) {
			continue;
		}

		const value = options.segmentValuesMm?.[i];
		const text = value === undefined ? letter : `${letter} ${value}`;

		const midX = (a.x + b.x) / 2;
		const midY = (a.y + b.y) / 2;
		const dx = b.x - a.x;
		const dy = b.y - a.y;
		const length = Math.hypot(dx, dy) || 1;

		// Outward normal: perpendicular to the segment, pointing away from the shape.
		let normalX = -dy / length;
		let normalY = dx / length;

		if ((centroidX - midX) * normalX + (centroidY - midY) * normalY > 0) {
			normalX = -normalX;
			normalY = -normalY;
		}

		/*
		 * A floor under the slide. Proportional slide alone strands the label on a
		 * very short flange: a 10mm web between two 150mm sides gives a 3-unit
		 * step, which cannot escape the two badges sitting on its own endpoints.
		 * The sweep test found exactly that, at five geometries.
		 */
		const slideStep = Math.max(Math.min(length * 0.28, 46), 34);
		let chosen: Annotation | null = null;
		let fallback: Annotation = { text, x: midX, y: midY };
		let fewestHits = Infinity;

		for (let out = 0; out < 14 && chosen === null; out++) {
			const distance = 20 + out * 13;

			for (const slide of [
				0,
				slideStep,
				-slideStep,
				slideStep * 2,
				-slideStep * 2,
				slideStep * 3,
				-slideStep * 3,
			]) {
				const candidate = {
					text,
					x: midX + normalX * distance + (dx / length) * slide,
					y: midY + normalY * distance + (dy / length) * slide,
				};

				const asBox = textBox(text, candidate.x, candidate.y, DRAW.labelFont);
				const hits = obstructions(asBox, placed, strokes);

				// Least obstructed, not last tried — see the badge loop above.
				if (hits < fewestHits) {
					fewestHits = hits;
					fallback = candidate;
				}

				if (hits === 0) {
					chosen = candidate;
					break;
				}
			}
		}

		const annotation = chosen ?? fallback;

		segmentLabels.push(annotation);
		placed.push(textBox(text, annotation.x, annotation.y, DRAW.labelFont));
	}

	const extent = union([...occupied, ...placed]);
	const width = extent.maxX - extent.minX + DRAW.margin * 2;
	const height = extent.maxY - extent.minY + DRAW.margin * 2;

	return {
		path,
		hemPath,
		segmentLabels,
		bendLabels,
		viewBox: `${(extent.minX - DRAW.margin).toFixed(2)} ${(extent.minY - DRAW.margin).toFixed(2)} ${width.toFixed(2)} ${height.toFixed(2)}`,
		width,
		height,
		strokes,
	};
}

/**
 * Every annotation box in a projection, for tests and for the collision audit.
 *
 * Exported so the guarantee "labels never overlap" can be asserted rather than
 * eyeballed, at every geometry the configurator can produce.
 */
export function annotationBoxes(projection: Projection): { label: string; box: Box }[] {
	return [
		...projection.bendLabels.map((a) => ({ label: `bend ${a.text}`, box: discBox(a.x, a.y) })),
		...projection.segmentLabels.map((a) => ({
			label: `segment ${a.text}`,
			box: textBox(a.text, a.x, a.y, DRAW.labelFont),
		})),
	];
}

/**
 * Every annotation that touches the part it is describing.
 *
 * THE GUARANTEE USED TO READ WIDER THAN THE ENFORCEMENT. `hasOverlap` compares
 * annotations to each other and the record called that "annotations never
 * collide" — silent about the outline, which nothing checked at all, so a
 * dimension could lie straight across the metal it dimensioned and the sweep
 * would pass. Reported by label so a failure names what is touching what.
 */
export function partCollisions(projection: Projection): string[] {
	const touching: string[] = [];

	for (const { label, box } of annotationBoxes(projection)) {
		for (const [from, to] of projection.strokes) {
			if (segmentHitsBox(from, to, box, STROKE_HALF)) {
				touching.push(label);
				break;
			}
		}
	}

	return touching;
}

/** True when any two annotations in a projection overlap. */
export function hasOverlap(projection: Projection): boolean {
	const boxes = annotationBoxes(projection);

	for (let i = 0; i < boxes.length; i++) {
		for (let j = i + 1; j < boxes.length; j++) {
			const first = boxes[i];
			const second = boxes[j];

			if (
				first !== undefined &&
				second !== undefined &&
				intersects(first.box, second.box, 0)
			) {
				return true;
			}
		}
	}

	return false;
}
